import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Grid,
	Paper,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeIcon from "@mui/icons-material/Badge";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PhoneIcon from "@mui/icons-material/Phone";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import BusinessIcon from "@mui/icons-material/Business";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AvatarPersona } from "./AvatarPersona";
import { getDetallePersona } from "../../services/apis";
import { SubHeader } from "../../utils/componentes-utiles/SubHeader";

const formatDateTime = (value) => {
	if (!value) return "-";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;

	return date.toLocaleString("es-ES", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const getEstadoColor = (estado) => {
	if (estado === "validado") return "success";
	if (estado === "no_reconocido") return "error";
	return "warning";
};

const getInitialPersona = (statePersona, idUsuario) => {
	if (statePersona?.id) return statePersona;

	return {
		id: idUsuario,
		nombre_completo: "Persona",
		imagen_url: null,
	};
};

const InfoField = ({ icon, label, value }) => (
	<Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
		<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
				{icon}
				<Typography variant="subtitle2" color="text.secondary">
					{label}
				</Typography>
			</Box>
			<Typography variant="body1">{value || "-"}</Typography>
		</Paper>
	</Grid>
);

export const DetallePersona = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const token =
		localStorage.getItem("accessToken") ||
		sessionStorage.getItem("accessToken") ||
		"";

	const idUsuario = Number(id);
	const [tab, setTab] = useState("info");
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");
	const [detalle, setDetalle] = useState(() => ({
		persona: getInitialPersona(location.state?.persona, idUsuario),
		historial: [],
		documentacion: [],
	}));

	const persona = detalle.persona;
	const historial = detalle.historial || [];
	const tieneAvatar = !!persona?.imagen_url;

	const subtitulo = useMemo(() => {
		const partes = [persona?.nombre_rol, persona?.nombre_departamento].filter(Boolean);
		return partes.length > 0 ? partes.join(" · ") : "Sin rol o departamento";
	}, [persona?.nombre_departamento, persona?.nombre_rol]);

	useEffect(() => {
		let active = true;

		const cargarDetalle = async () => {
			setLoaded(false);
			setError("");

			try {
				const response = await getDetallePersona(idUsuario);

				if (!active) return;

				if (response?.success) {
					setDetalle({
						persona: response.data?.persona || getInitialPersona(location.state?.persona, idUsuario),
						historial: response.data?.historial || [],
						documentacion: response.data?.documentacion || [],
					});
				} else {
					setError(response?.message || "No se pudo cargar la persona");
				}
			} catch (err) {
				console.error("Error cargando detalle de persona:", err);
				if (active) {
					setError("No se pudo cargar el detalle de la persona");
				}
			} finally {
				if (active) {
					setLoaded(true);
				}
			}
		};

		if (Number.isFinite(idUsuario) && idUsuario > 0) {
			cargarDetalle();
		} else {
			setError("ID de persona no valido");
			setLoaded(true);
		}

		return () => {
			active = false;
		};
	}, [idUsuario, location.state?.persona]);

	const handleAvatarUploaded = (updatedPersona) => {
		setDetalle((current) => ({
			...current,
			persona: {
				...current.persona,
				...updatedPersona,
			},
		}));
	};

	const volver = () => {
		if (typeof location.state?.from === "string" && location.state.from.trim()) {
			navigate(location.state.from);
			return;
		}

		navigate("/personas");
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Detalle de persona" />
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 2 }}>
				<Button variant="text" startIcon={<ArrowBackIcon />} onClick={volver}>
					Volver
				</Button>
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 3 }}>
				<Paper variant="outlined" sx={{ width: "100%", p: { xs: 2, md: 3 } }}>
					<Grid container spacing={3} alignItems="center">
						<Grid
							item
							size={{ xs: 12, md: 3 }}
							sx={{ display: "flex", justifyContent: "center" }}
						>
							<AvatarPersona
								persona={persona}
								token={token}
								size={140}
								allowUpload
								onAvatarUploaded={handleAvatarUploaded}
							/>
						</Grid>

						<Grid item size={{ xs: 12, md: 9 }}>
							<Typography variant="h4" sx={{ mb: 0.5 }}>
								{persona?.nombre_completo || "-"}
							</Typography>

							<Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
								{subtitulo}
							</Typography>

							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
								<Chip label={`ID ${persona?.id || "-"}`} variant="outlined" />
								<Chip label={persona?.nif || "Sin NIF"} color="primary" variant="outlined" />
								{persona?.nombre_rol && <Chip label={persona.nombre_rol} />}
								{persona?.nombre_departamento && (
									<Chip label={persona.nombre_departamento} color="secondary" />
								)}
							</Box>

							<Typography variant="body2" color="text.secondary">
								{tieneAvatar
									? "Pulsa la foto para verla en grande o usa el icono de cámara para actualizarla."
									: "Pulsa el avatar o el icono de cámara para añadir una foto."}
							</Typography>
						</Grid>
					</Grid>

					<Divider sx={{ my: 3 }} />

					<Tabs
						value={tab}
						onChange={(_, value) => setTab(value)}
						variant="scrollable"
						allowScrollButtonsMobile
						sx={{ mb: 3 }}
					>
						<Tab label="Informacion" value="info" />
						<Tab label="Historial" value="historial" />
						<Tab label="Documentacion" value="docs" />
					</Tabs>

					{!loaded && (
						<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
							<CircularProgress />
						</Box>
					)}

					{loaded && error && <Alert severity="error">{error}</Alert>}

					{loaded && !error && tab === "info" && (
						<Grid container spacing={2}>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="Nombre"
								value={persona?.nombre_completo || "-"}
							/>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="NIF"
								value={persona?.nif || "-"}
							/>
							<InfoField
								icon={<AlternateEmailIcon color="primary" fontSize="small" />}
								label="Email"
								value={persona?.mail || "-"}
							/>
							<InfoField
								icon={<PhoneIcon color="primary" fontSize="small" />}
								label="Telefono"
								value={persona?.telefono || "-"}
							/>
							<InfoField
								icon={<AssignmentIndIcon color="primary" fontSize="small" />}
								label="Rol"
								value={persona?.nombre_rol || "-"}
							/>
							<InfoField
								icon={<ApartmentIcon color="primary" fontSize="small" />}
								label="Departamento"
								value={persona?.nombre_departamento || "-"}
							/>
						</Grid>
					)}

					{loaded && !error && tab === "historial" && (
						<Grid container direction="column" spacing={2}>
							{historial.length === 0 ? (
								<Grid item size={{ xs: 12 }}>
									<Alert severity="info">
										Esta persona todavia no tiene historial de partes de grupo visible.
									</Alert>
								</Grid>
							) : (
								historial.map((item) => (
									<Grid item size={{ xs: 12 }} key={`${item.id_cuadrilla}-${item.fecha_cuadrilla}`}>
										<Paper variant="outlined" sx={{ p: 2.25 }}>
											<Grid container spacing={2} alignItems="center">
												<Grid item size={{ xs: 12, md: 4 }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
														<GroupWorkIcon color="warning" fontSize="small" />
														<Typography variant="subtitle1">
															Grupo {item.id_cuadrilla}
														</Typography>
													</Box>
													<Typography variant="body2" color="text.secondary">
														Cabo: {item.nombre_cabo} {item.apellidos_cabo}
													</Typography>
												</Grid>

												<Grid item size={{ xs: 12, md: 3 }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
														<EventIcon color="primary" fontSize="small" />
														<Typography variant="body2">
															{formatDateTime(item.fecha_cuadrilla)}
														</Typography>
													</Box>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<BusinessIcon color="success" fontSize="small" />
														<Typography variant="body2">{item.nombre_cliente || "-"}</Typography>
													</Box>
												</Grid>

												<Grid item size={{ xs: 12, md: 5 }}>
													<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "start", md: "end" } }}>
														<Chip
															label={item.estado_label}
															color={getEstadoColor(item.estado)}
															size="small"
														/>
														<Chip
															label={Number(item.validado) === 1 ? "Validado" : "Sin validar"}
															variant="outlined"
															size="small"
														/>
														<Chip
															label={Number(item.revisado) === 1 ? "Revisado" : "Sin revisar"}
															variant="outlined"
															size="small"
														/>
														{!!item.puntuacion_comprobacion && (
															<Chip
																label={`${item.puntuacion_comprobacion}/10`}
																size="small"
																variant="outlined"
															/>
														)}
													</Box>
												</Grid>
											</Grid>
										</Paper>
									</Grid>
								))
							)}
						</Grid>
					)}

					{loaded && !error && tab === "docs" && (
						<Grid container spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Alert severity="info">
									La documentacion por persona en web todavia no tiene endpoint especifico.
									La pestaña queda preparada para conectarla cuando ese dato este disponible.
								</Alert>
							</Grid>

							<Grid item size={{ xs: 12 }}>
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
										<InsertDriveFileIcon color="action" />
										<Typography variant="subtitle2">Estado actual</Typography>
									</Box>
									<Typography variant="body2" color="text.secondary">
										Pendiente de integrar los documentos individuales del usuario {persona?.id || "-"}.
									</Typography>
								</Paper>
							</Grid>
						</Grid>
					)}
				</Paper>
			</Grid>
		</Grid>
	);
};
